import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  archiveScholarship,
  listScholarships,
  reorderScholarships,
  restoreScholarship,
} from '../../../../services/scholarships';
import type { ScholarshipListItem } from '../../../../types/scholarships';
import { Button } from '../../../../components/Button';
import { TablePagination } from '../../../../components/TablePagination';
import { ConfirmModal } from '../../../../components/ConfirmModal';
import { getSafeErrorMessage } from '../../../../utils/errorHandler';
import { tourAnchor } from '../../../tours/tourAnchors';
import { useTour } from '../../../tours/useTour';
import { AddScholarshipPanel } from './AddScholarshipPanel';
import { DeleteScholarshipModal } from './DeleteScholarshipModal';
import { DuplicateScholarshipModal } from './DuplicateScholarshipModal';
import { NameScholarshipModal } from './NameScholarshipModal';
import {
  ScholarshipsTable,
  type ScholarshipMove,
  type ScholarshipRowAction,
} from './ScholarshipsTable';
import { applySectionOrder, moveItem, reorderPayloadIds } from './scholarshipOrdering';
import { isConflictError } from './useRosterPlayers';
import './ScholarshipsSection.css';

/*
  Both sections have to stay reachable. With one list per kind and nothing capping the first,
  a program with thirty scholarship rosters pushes the Recruiting Board heading off the bottom
  of the screen, and the second section reads as missing rather than as further down.
*/
const ROSTERS_PER_PAGE = 5;

/*
  Another coach created, archived or deleted a board while this one was dragging, so the set the
  API was sent no longer describes the list. Refetched rather than retried: the order just sent
  is about a list that no longer exists, and sending it again would be guessing at what should
  happen to the board it does not mention.
*/
const REORDER_CONFLICT_MESSAGE =
  'The list of boards changed while you were reordering, so it has been reloaded. Try again.';

/** A queued order, with the view it was computed against. See showArchivedRef. */
type PendingOrder = { order: ScholarshipListItem[]; showArchived: boolean };

type ConfirmAction = { kind: 'archive' | 'restore'; scholarship: ScholarshipListItem };
type FormAction = { kind: 'rename' | 'delete' | 'duplicate'; scholarship: ScholarshipListItem };

export const ScholarshipsSection = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<ScholarshipListItem[] | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedCount, setArchivedCount] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [managementPage, setManagementPage] = useState(1);
  const [recruitingPage, setRecruitingPage] = useState(1);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [formAction, setFormAction] = useState<FormAction | null>(null);
  const [error, setError] = useState<string | null>(null);

  /*
    Moves a coach has made. A read that started before one of them describes a list that is now
    out of date, so it is thrown away rather than rendered. Mirrors useRosterPlayers' generation.
  */
  const orderTicket = useRef(0);
  // Reads, so an older list cannot land on top of a newer one.
  const loadTicket = useRef(0);

  /*
    Loads without touching the message, so a reorder conflict notice survives its own refetch.

    Tracked twice over. The recovery read this runs after a failed move used to be unguarded, and
    that lost orders for good: the read goes out, the coach moves again, THAT move saves, and then
    the old read lands and puts the pre-move list on screen. The next move is computed from that
    stale list and written back, so the newer order is gone with nothing to say so. A response is
    applied only when it is still the newest read AND no move has happened since it started.
  */
  const load = useCallback(async () => {
    const ticket = ++loadTicket.current;
    const movesAtStart = orderTicket.current;
    const stale = () => ticket !== loadTicket.current || movesAtStart !== orderTicket.current;
    try {
      const list = await listScholarships(showArchived);
      if (stale()) return;
      setItems(list);
      if (list.length === 0 && !showArchived) {
        const all = await listScholarships(true);
        if (stale()) return;
        setArchivedCount(all.length);
      } else {
        setArchivedCount(null);
      }
    } catch (e) {
      if (stale()) return;
      setError(getSafeErrorMessage(e, 'Failed to load rosters'));
      setItems([]);
    }
  }, [showArchived]);

  const refresh = useCallback(async () => {
    setError(null);
    await load();
  }, [load]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /*
    The board list is its own view of the rosters feature, with a page tour and no data tour. Its
    steps point at the New roster button and the list area, and both are on screen whether the
    org has boards or not, so the tour only waits for the first load.
  */
  useTour('rosters', {
    ready: items !== null,
    hasData: false,
    pageTourKey: 'rosters.index',
    dataTourKey: null,
  });

  /*
    The newest order still waiting to be written, and whether a write is already out.

    The save is EXCLUSIVE with latest-wins coalescing: one PATCH at a time, and a press made while
    one is in flight only replaces what goes out next. Letting every press send its own full-list
    PATCH meant two of them could reach the server out of order, leaving it holding the older order
    while the screen showed the newer one -- and nothing looks at that, so the move read as saved
    and was gone on the next load. Deciding which REPLY to apply never helped, because the damage
    is done by which REQUEST lands last. Mobile's saveOrder is guarded the same way.
  */
  const pendingOrder = useRef<PendingOrder | null>(null);
  const savingOrder = useRef(false);

  /*
    Which list the queued order was computed against. Archived boards are a different list, not a
    longer one, so a reply describing the unarchived list must not be rendered into the archived
    view -- it does so without an error, and the coach has to toggle twice to get the real list
    back.
  */
  const showArchivedRef = useRef(showArchived);
  showArchivedRef.current = showArchived;

  /*
    Reads the list again after a save this build could not use. Deliberately NOT awaited: it is a
    read, the exclusivity above is about writes, and holding the lock across it would leave a slow
    or hung GET blocking every later move -- the coach presses Move up and nothing goes out at all.
    A move made while it is in flight invalidates it through `load`'s own ticket instead.

    Read through a ref rather than closed over: `load` is rebuilt whenever the archived toggle
    moves, and flushOrder holds whichever resync existed when IT was built, so a direct dependency
    meant the recovery read fetched the view the coach has already left.
  */
  const loadRef = useRef(load);
  loadRef.current = load;
  const resync = useCallback(() => {
    void loadRef.current();
  }, []);

  const flushOrder = useCallback(async () => {
    if (savingOrder.current) return;
    savingOrder.current = true;
    try {
      while (pendingOrder.current !== null) {
        const next = pendingOrder.current;
        pendingOrder.current = null;
        // The view this order was computed against. A toggle since then means the reply is about
        // a different list from the one on screen, whether it succeeded or not.
        const viewChanged = () => next.showArchived !== showArchivedRef.current;
        try {
          const saved = await reorderScholarships(reorderPayloadIds(next.order));
          // A newer order is already queued, so this reply describes a list the coach has moved
          // on from. Its own save says what ends up on screen.
          if (pendingOrder.current !== null) continue;
          if (viewChanged()) {
            resync();
            continue;
          }
          // Guarded rather than trusted: web and the API deploy separately, and a body this build
          // cannot read is a reason to reload the list, not to render nothing.
          if (Array.isArray(saved) && saved.every((s) => typeof s?.id === 'string')) {
            setItems(saved);
          } else {
            resync();
          }
        } catch (e) {
          if (pendingOrder.current !== null) continue;
          if (viewChanged()) {
            resync();
            continue;
          }
          setError(
            isConflictError(e)
              ? REORDER_CONFLICT_MESSAGE
              : getSafeErrorMessage(e, 'Failed to save the new order'),
          );
          // Resyncs from the server rather than rolling back to a snapshot, which by then may be
          // two moves out of date.
          resync();
        }
      }
    } finally {
      savingOrder.current = false;
    }
  }, [resync]);

  /*
    Moves a board inside its section and queues the whole order.

    Optimistic, because the alternative is a row that does not move until a round trip lands, on a
    control a coach presses several times in a row.
  */
  const handleMove = (section: ScholarshipListItem[], move: ScholarshipMove) => {
    if (items === null) return;
    const orderedSectionIds = moveItem(section, move.from, move.to).map((s) => s.id);
    const next = applySectionOrder(items, orderedSectionIds);
    setItems(next);
    setError(null);
    orderTicket.current += 1;
    pendingOrder.current = { order: next, showArchived };
    void flushOrder();
  };

  const handleConfirm = async () => {
    if (!confirmAction) return;
    const { kind, scholarship } = confirmAction;
    try {
      await (kind === 'archive' ? archiveScholarship : restoreScholarship)(scholarship.id);
      setConfirmAction(null);
      await refresh();
    } catch (e) {
      setError(getSafeErrorMessage(e, `Failed to ${kind} roster`));
    }
  };

  const handleRowAction = (action: ScholarshipRowAction, s: ScholarshipListItem) => {
    if (action === 'archive' || action === 'restore') {
      setConfirmAction({ kind: action, scholarship: s });
      return;
    }
    setFormAction({ kind: action, scholarship: s });
  };

  const handleCreateSuccess = (created: { id: string }) => {
    setCreateOpen(false);
    navigate(`/rosters/${created.id}`);
  };

  if (items === null) {
    return (
      <div className="scholarships-section scholarships-section--loading">
        Loading rosters...
      </div>
    );
  }

  const isEmpty = items.length === 0 && !showArchived;

  /*
    The API's reorder takes the caller's non-archived boards and refuses anything else, so while
    archived rows are on screen there is no order to express: a coach would be dragging rows the
    save cannot mention. The controls go away and a line says why, rather than moving a row and
    then reporting a failure.
  */
  const canReorder = !showArchived;

  /*
    One list per kind of board rather than one list with a badge. Coaches work in one or the
    other, so the split is how they read the page. Anything not explicitly a recruiting board
    counts as roster management: Scholarship is the default, and rosters created before the
    purpose field existed have none at all.
  */
  const groups = [
    {
      key: 'management',
      title: 'Roster Management',
      items: items.filter((s) => s.purpose !== 'Recruiting'),
      page: managementPage,
      onPageChange: setManagementPage,
      emptyText: showArchived
        ? 'No archived rosters.'
        : 'No rosters yet. Create one to plan scholarships and roster spots.',
    },
    {
      key: 'recruiting',
      title: 'Recruiting Board',
      items: items.filter((s) => s.purpose === 'Recruiting'),
      page: recruitingPage,
      onPageChange: setRecruitingPage,
      emptyText: showArchived
        ? 'No archived recruiting boards.'
        : 'No recruiting boards yet. Create one to track position needs and commitments.',
    },
  ];

  return (
    <div className="scholarships-section">
      <header className="scholarships-section__header">
        <h1 className="scholarships-section__title">Roster Planning</h1>
        <div className="scholarships-section__toolbar">
          <label className="scholarships-section__archived-toggle">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => {
                setShowArchived(e.target.checked);
                // A different list — page 3 of the one being replaced means nothing in it.
                setManagementPage(1);
                setRecruitingPage(1);
              }}
            />
            Show archived
          </label>
          <Button
            variant="secondary"
            onClick={() => navigate('/rosters/roster-images')}
          >
            View roster images
          </Button>
          <Button
            variant="primary"
            onClick={() => setCreateOpen(true)}
            {...tourAnchor('rosters.index.new')}
          >
            + New roster
          </Button>
        </div>
      </header>

      {error && (
        <div className="scholarships-section__error" role="alert">{error}</div>
      )}

      {!canReorder && !isEmpty && (
        <p className="scholarships-section__reorder-note">
          Turn off Show archived to put your boards back in the order you want them.
        </p>
      )}

      {isEmpty ? (
        <div className="scholarships-section__empty" {...tourAnchor('rosters.index.list')}>
          <h2 className="scholarships-section__empty-title">No rosters yet</h2>
          <p className="scholarships-section__empty-text">
            Build your roster and track player details by creating your first one.
          </p>
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            Build your first roster
          </Button>
          {archivedCount !== null && archivedCount > 0 && (
            <p className="scholarships-section__archived-hint">
              You have {archivedCount} archived roster{archivedCount === 1 ? '' : 's'}.{' '}
              <button
                type="button"
                className="scholarships-section__link-button"
                onClick={() => setShowArchived(true)}
              >
                Show archived
              </button>
            </p>
          )}
        </div>
      ) : items.length === 0 && showArchived ? (
        <div className="scholarships-section__empty" {...tourAnchor('rosters.index.list')}>
          <h2 className="scholarships-section__empty-title">No archived rosters</h2>
          <p className="scholarships-section__empty-text">
            Nothing has been archived yet.
          </p>
        </div>
      ) : (
        <div className="scholarships-section__groups" {...tourAnchor('rosters.index.list')}>
          {groups.map((group) => {
            /*
              Clamped rather than reset by an effect: archiving or deleting the last row on the
              last page shrinks the list under the page a coach is standing on, and an
              out-of-range page renders an empty table with a pager above it saying otherwise.
            */
            const lastPage = Math.max(1, Math.ceil(group.items.length / ROSTERS_PER_PAGE));
            const page = Math.min(group.page, lastPage);
            const pageItems = group.items.slice(
              (page - 1) * ROSTERS_PER_PAGE,
              page * ROSTERS_PER_PAGE,
            );

            return (
              <section key={group.key} className="scholarships-section__group">
                <h2 className="scholarships-section__group-title">{group.title}</h2>
                {group.items.length === 0 ? (
                  <p className="scholarships-section__group-empty">{group.emptyText}</p>
                ) : (
                  <>
                    <ScholarshipsTable
                      items={pageItems}
                      ariaLabel={group.title}
                      pageOffset={(page - 1) * ROSTERS_PER_PAGE}
                      sectionCount={group.items.length}
                      onMove={
                        canReorder
                          ? (move) => {
                              /*
                                Follow the row. A move off the top or the bottom of a page lands the
                                board on the page next door, and a coach who pressed Move up and
                                watched the row vanish would reasonably read that as a delete.
                              */
                              const landsOn = Math.floor(move.to / ROSTERS_PER_PAGE) + 1;
                              if (landsOn !== page) group.onPageChange(landsOn);
                              handleMove(group.items, move);
                            }
                          : undefined
                      }
                      onRowAction={handleRowAction}
                    />
                    {/* Renders nothing at all while a section fits on one page. */}
                    <TablePagination
                      currentPage={page}
                      pageSize={ROSTERS_PER_PAGE}
                      totalCount={group.items.length}
                      onPageChange={group.onPageChange}
                    />
                  </>
                )}
              </section>
            );
          })}
        </div>
      )}

      <AddScholarshipPanel
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      {formAction?.kind === 'rename' && (
        <NameScholarshipModal
          scholarship={formAction.scholarship}
          onClose={() => setFormAction(null)}
          onSuccess={() => {
            setFormAction(null);
            refresh();
          }}
        />
      )}

      {formAction?.kind === 'duplicate' && (
        <DuplicateScholarshipModal
          scholarship={formAction.scholarship}
          onClose={() => setFormAction(null)}
          onSuccess={(copy) => {
            setFormAction(null);
            navigate(`/rosters/${copy.id}`);
          }}
        />
      )}

      {formAction?.kind === 'delete' && (
        <DeleteScholarshipModal
          scholarship={formAction.scholarship}
          onClose={() => setFormAction(null)}
          onSuccess={() => {
            setFormAction(null);
            refresh();
          }}
        />
      )}

      <ConfirmModal
        isOpen={confirmAction !== null}
        title={confirmAction?.kind === 'archive' ? 'Archive roster?' : 'Restore roster?'}
        message={
          confirmAction?.kind === 'archive'
            ? `Archive '${confirmAction.scholarship.name}'? You can restore it later from Show archived.`
            : confirmAction
              ? `Restore '${confirmAction.scholarship.name}'?`
              : ''
        }
        confirmText={confirmAction?.kind === 'archive' ? 'Archive' : 'Restore'}
        onConfirm={handleConfirm}
        onClose={() => setConfirmAction(null)}
      />
    </div>
  );
};
